import { FC, ReactElement, ReactNode, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import ActivityFrame from "../../../components/activity-frame/activity-frame";
import Breadcrumb from "../../../components/breadcrumb/breadcrumb";
import { useDialog } from "../../../components/dialog/DialogContext";
import ReportModal from "../../../components/report/ReportModal";
import Tabs, { getTabsListFromLabels } from "../../../components/tabs/tabs";
import { getCountsCategory, REPORT_TYPE_CATEGORY, ReportType, TYPE_PER_REPORT_TYPE } from "../../../constants/system-constants";
import { useLocale } from '../../../contexts/localeContext';
import { TranslationKeys } from '../../../contexts/TranslationKeys';
import { Category } from "../../../models/Category";
import { Report } from "../../../models/Report";
import SystemStat from "../../../models/SystemStat";
import { storeReport } from "../../../services/reportService";
import { getSystemStats } from "../../../services/systemService";
import { getErrorMessage, showError } from "../../../services/utilities/ServiceError";
import { IAppState } from "../../../store/types";
import { getItemPath } from "../../../utilities/reportUtil";
import AllReport from "./AllReport";

interface ReportsMainProps {
    reportType: ReportType.TRANSACTIONAL | ReportType.TIME_AND_ATTENDANCE;
}

const ReportsMain: FC<ReportsMainProps> = ({ reportType }) => {
    const [selected, setSelected] = useState(0);
    const [busy, setBusy] = useState(false);
    const history = useHistory();
    const [categories, setCategories] = useState<Category[]>([]);
    const [tabContent, setTabContent] = useState<ReactNode[]>([]);
    const { translate } = useLocale();
    const [showModal, setShowModal] = useState(false);
    const user = useSelector((state: IAppState) => (state.userInfo));
    const dialog = useDialog();
    const [currentItem, setCurrentItem] = useState<Report>({
        id: 0,
        name: '',
        typeId: "",
        reportSubTypeId: ""
    })
    
    useEffect(() => {
        buildCategoriesData();
    }, []);

    const getReportTypeCategory = () => {
        switch (reportType) {
            case ReportType.TRANSACTIONAL:
                return REPORT_TYPE_CATEGORY.TRANSACTION_RPTS;
            case ReportType.TIME_AND_ATTENDANCE:
                return REPORT_TYPE_CATEGORY.TIME_AND_ATTENDANCE_RPTS;
        }
    }

    const getReportDisplayName = () => {
        switch (reportType) {
            case ReportType.TRANSACTIONAL:
                return translate.byKey(TranslationKeys.transactions);
            case ReportType.TIME_AND_ATTENDANCE:
                return translate.byKey(TranslationKeys.timeAndAttendance);
        }
    }

    const getReportHeading = () => {
        switch (reportType) {
            case ReportType.TRANSACTIONAL:
                return translate.byKey(TranslationKeys.transactions);
            case ReportType.TIME_AND_ATTENDANCE:
                return translate.byKey(TranslationKeys.timeAndAttendance);
        }
    }

    const buildCategoriesData = async () => {
        try {
            setBusy(true);
            const showTabCounts = window.sessionStorage.getItem("showTabCounts") === "1";
            const systemStat: SystemStat | undefined = showTabCounts ? await getSystemStats(TYPE_PER_REPORT_TYPE) : undefined;
            const count = systemStat ? getCountsCategory(systemStat, getReportTypeCategory()) : 0;
            setCategories([
                {
                    displayName: getReportDisplayName(),
                    heading: getReportHeading(),
                    value: "",
                    id: 0,
                    count: count
                },
            ]);
            setBusy(false);
        } catch (error) {
            showError(error, toast.warn);
        }
    }

    useEffect(() => {
        if (categories.length > 0) {
            const newTabContent: ReactElement[] = [
                <AllReport
                    setCurrentItem={setCurrentItem}
                    setBusy={setBusy}
                    categories={categories}
                    setCategories={setCategories}
                    openModal={setShowModal}
                    isModalOpen={showModal}
                    reportType={reportType} />];
            setTabContent(newTabContent);
        }
    }, [categories]);

    const onModalClose = () => {
        setCurrentItem({
            id: 0,
            name: '',
            typeId: "",
            reportSubTypeId: ""
        })
        setShowModal(false)
    }

    const saveItem = (values) => {
        const report: Report = {
            id: values.id ?? 0,
            name: values.name,
            description: values.description,
            typeId: values.typeId,
            reportSubTypeId: values.reportSubTypeId
        }

        const isNew = values.id ? false : true;

        setBusy(true);
        storeReport(report, user.id, isNew)
            .then(async (value) => {
                toast.success(translate.byKey(TranslationKeys.reportSaved));
                if (currentItem.id === 0) {
                    setCategories([{ ...categories[0], count: (categories[0]?.count ?? 1) + 1 }]);
                }
                setShowModal(false);
            })
            .catch(failure => {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(failure) });
            })
            .finally(() => {
                setBusy(false);
            });
    }

    return (
        <ActivityFrame
            busy={busy}
            className="flex flex-grow overflow-y-hidden">
            <div className="pt-4 pl-4 pb-4 flex flex-col flex-grow ">
                <Breadcrumb
                    path={[
                        {
                            value: "/access-control/reports",
                            displayName: translate.byKey(TranslationKeys.reports),
                        },
                        {
                            value: getItemPath(reportType, true),
                            displayName: getReportDisplayName(),
                        },
                    ]}
                    onClick={(item) => history.push(item.value)} />
                <Tabs
                    className="overflow-y-hidden"
                    tabs={getTabsListFromLabels(categories)}
                    onSelect={(i) => setSelected(i)}
                    setSelectedTab={setSelected}
                    selected={selected}
                    onAdd={() => { }}
                    add={false}
                    tabStyle={{}}>
                    {tabContent[selected]}
                    <div className="flex flex-row justify-end button-cluster">

                    </div>
                </Tabs>
            </div>
            <ReportModal currentItem={currentItem} isOpen={showModal} onClose={onModalClose} saveReport={saveItem} />
        </ActivityFrame>
    );
};

export default ReportsMain;
